import { autoPlacement, offset, Placement, ReferenceType, useFloating, UseFloatingOptions } from '@floating-ui/react';
import { useState } from 'react';

/**
 * Custom whileElementsMounted handler that prevents modal repositioning when content changes.
 * Only updates position when the reference element changes or when scrolling occurs.
 * This keeps the modal in a stable position even as its content grows or shrinks.
 */
const createStablePositioning = (referenceEl: ReferenceType, floatingEl: HTMLElement | null, update: () => void) => {
  // Only update position when reference element changes, not when content changes
  const resizeObserver = new ResizeObserver(() => {
    // Only observe reference element size changes (check if it's a real DOM element)
    if (referenceEl && 'getBoundingClientRect' in referenceEl && referenceEl instanceof Element) {
      update();
    }
  });

  // Only observe if referenceEl is a real DOM element (not a virtual element)
  if (referenceEl && 'getBoundingClientRect' in referenceEl && referenceEl instanceof Element) {
    resizeObserver.observe(referenceEl);
  }

  // Listen for scroll events that might affect the reference element
  const scrollHandler = () => update();
  window.addEventListener('scroll', scrollHandler, true);

  return () => {
    resizeObserver.disconnect();
    window.removeEventListener('scroll', scrollHandler, true);
  };
};

export const useErrorModal = ({
  placement = 'right',
  allowedPlacements = ['right', 'left', 'top', 'bottom'],
}: { placement?: UseFloatingOptions['placement']; allowedPlacements?: Array<Placement> } = {}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { refs, floatingStyles } = useFloating({
    open: showErrorModal,
    onOpenChange: setShowErrorModal,
    placement,
    strategy: 'fixed',
    whileElementsMounted: createStablePositioning,
    middleware: [
      offset(8),
      autoPlacement({
        allowedPlacements: allowedPlacements,
      }),
    ],
  });

  return { setShowErrorModal, showErrorModal, refs, floatingStyles };
};
